import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import GroupsIcon from "@mui/icons-material/Groups";
import PinDropIcon from "@mui/icons-material/PinDrop";
import BusinessIcon from "@mui/icons-material/Business";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import HistoryIcon from "@mui/icons-material/History";

import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import HistorialCliente from "../../historialCliente/pages/HistorialCliente";
import { listarUsuarios, crearUsuario, editarUsuario } from "../services/trsRastreoService";

const USU_TIPOS = [
  { value: 1, label: "Administrador" },
  { value: 2, label: "Inicio 1ra pestaña (Cliente)" },
  { value: 3, label: "Inicio 2da pestaña (Cliente)" },
];

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "0.78rem",
  padding: "6px 8px",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const cellSx = { py: 0.6, px: 1, fontSize: "0.8rem" };

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 32, fontSize: "0.8rem" },
  "& .MuiInputBase-input": { py: 0.65 },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
};

const getErrorMessage = (error) =>
  error?.response?.data?.error || error?.response?.data?.message || error?.message || "No se pudo cargar la informacion";

const CountBadge = ({ value, icon, color = "info" }) => (
  value > 0 ? (
    <Chip
      icon={icon}
      label={value}
      size="small"
      color={color}
      variant="outlined"
      sx={{ height: 22, fontSize: "0.72rem", fontWeight: 700, "& .MuiChip-icon": { fontSize: 14, ml: "6px" } }}
    />
  ) : (
    <Typography variant="caption" color="text.disabled">—</Typography>
  )
);

const PasswordCell = ({ password, visible, onToggleVisible, onCopy }) => {
  if (!password) return <Typography variant="caption" color="text.disabled">—</Typography>;
  return (
    <Stack direction="row" spacing={0.25} alignItems="center">
      <Typography variant="body2" sx={{ fontSize: "0.8rem", fontFamily: "monospace" }}>
        {visible ? password : "*****"}
      </Typography>
      <Tooltip title={visible ? "Ocultar" : "Mostrar"}>
        <IconButton size="small" onClick={onToggleVisible} sx={{ p: 0.25 }}>
          {visible ? <VisibilityOffIcon sx={{ fontSize: 15 }} /> : <VisibilityIcon sx={{ fontSize: 15 }} />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Copiar">
        <IconButton size="small" onClick={onCopy} sx={{ p: 0.25 }}>
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

export default function TrsRastreoUsuariosPage() {
  const [clientes, setClientes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [buscar, setBuscar] = useState("");
  const [buscarActivo, setBuscarActivo] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(30);
  const [detalle, setDetalle] = useState(null);
  const cuadrillasRelacionadas = (detalle?.relacion_usuarios || []).filter((r) => r.table_rel === "cuadrillas");
  const geocercasRelacionadas = (detalle?.relacion_usuarios || []).filter((r) => r.table_rel === "geocercas");
  const [passwordsVisibles, setPasswordsVisibles] = useState({});
  const [copiado, setCopiado] = useState(false);
  const [nuevoUsuarioCliente, setNuevoUsuarioCliente] = useState(null);
  const [editUsuarioId, setEditUsuarioId] = useState(null);
  const [nuevoUsuarioForm, setNuevoUsuarioForm] = useState({
    usu_nombre: "", usu_username: "", usu_password: "", usu_email: "", usu_activo: true, usu_tipo: 1, usu_cliente: null,
  });
  const [guardandoUsuario, setGuardandoUsuario] = useState(false);
  const [errorUsuario, setErrorUsuario] = useState(null);
  const [historialCliente, setHistorialCliente] = useState(null);

  const abrirHistorial = (cliente) => {
    setHistorialCliente(cliente);
  };

  const abrirAdicionarUsuario = (cliente) => {
    setEditUsuarioId(null);
    setNuevoUsuarioForm({
      usu_nombre: "", usu_username: "", usu_password: "", usu_email: "",
      usu_activo: true, usu_tipo: 1, usu_cliente: cliente.cod_servicio_cliente,
    });
    setErrorUsuario(null);
    setNuevoUsuarioCliente(cliente);
  };

  const abrirEditarUsuario = (usuario, cliente) => {
    setEditUsuarioId(usuario.id);
    setNuevoUsuarioForm({
      usu_nombre: usuario.usu_nombre || "",
      usu_username: usuario.usu_username || "",
      usu_password: "",
      usu_email: usuario.usu_email || "",
      usu_activo: usuario.usu_activo !== false,
      usu_tipo: usuario.usu_tipo || 1,
      usu_cliente: usuario.usu_cliente,
    });
    setErrorUsuario(null);
    setNuevoUsuarioCliente({
      cod_servicio_cliente: usuario.usu_cliente,
      razon_social: cliente?.razon_social,
    });
  };

  const guardarNuevoUsuario = async () => {
    if (!nuevoUsuarioCliente) return;
    if (!nuevoUsuarioForm.usu_nombre.trim() || !nuevoUsuarioForm.usu_username.trim()) {
      setErrorUsuario("Nombre y usuario son obligatorios");
      return;
    }
    if (!editUsuarioId && !nuevoUsuarioForm.usu_password.trim()) {
      setErrorUsuario("La contraseña es obligatoria");
      return;
    }
    setGuardandoUsuario(true);
    setErrorUsuario(null);
    try {
      if (editUsuarioId) {
        const payload = {
          usu_nombre: nuevoUsuarioForm.usu_nombre,
          usu_username: nuevoUsuarioForm.usu_username,
          usu_email: nuevoUsuarioForm.usu_email,
          usu_cliente: nuevoUsuarioForm.usu_cliente,
          usu_activo: nuevoUsuarioForm.usu_activo,
          usu_tipo: nuevoUsuarioForm.usu_tipo,
        };
        if (nuevoUsuarioForm.usu_password.trim()) {
          payload.usu_password = nuevoUsuarioForm.usu_password;
        }
        await editarUsuario(editUsuarioId, payload);
      } else {
        await crearUsuario({
          usu_nombre: nuevoUsuarioForm.usu_nombre,
          usu_username: nuevoUsuarioForm.usu_username,
          usu_password: nuevoUsuarioForm.usu_password,
          usu_email: nuevoUsuarioForm.usu_email,
          usu_cliente: nuevoUsuarioForm.usu_cliente,
          usu_tipo: nuevoUsuarioForm.usu_tipo,
        });
      }
      setNuevoUsuarioCliente(null);
      setEditUsuarioId(null);
      await cargar();
    } catch (e) {
      setErrorUsuario(getErrorMessage(e));
    } finally {
      setGuardandoUsuario(false);
    }
  };

  const togglePassword = (id) => setPasswordsVisibles((p) => ({ ...p, [id]: !p[id] }));

  const copiarPassword = async (password) => {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      setCopiado(true);
    } catch {
      // navigator.clipboard puede no estar disponible (http sin TLS, permisos, etc.)
    }
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listarUsuarios({ buscar: buscarActivo, page: page + 1, pageSize });
      const body = res?.data;
      const data = body?.data ?? [];
      setClientes(Array.isArray(data) ? data : []);
      setTotal(Number(body?.total || 0));
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [buscarActivo, page, pageSize]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleBuscar = () => {
    setPage(0);
    setBuscarActivo(buscar.trim());
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="TRS Rastreo — Usuarios"
        subtitle="Credenciales y permisos, por cliente de SORT"
        backTo="/trsrastreo"
        actions={(
          <Button size="small" variant="contained" startIcon={<RefreshIcon />} onClick={cargar} disabled={loading}>
            Actualizar
          </Button>
        )}
      />

      <Box sx={{ p: { xs: 1, md: 1.5 } }}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 1 }}>
          <TextField
            size="small"
            label="Buscar cliente"
            placeholder="Razon social, servicio, grupo, cod. cliente..."
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleBuscar(); }}
            sx={{ ...compactFieldSx, width: 320 }}
          />
          <Button size="small" variant="contained" startIcon={<SearchIcon />} onClick={handleBuscar} sx={{ minHeight: 32 }}>
            Buscar
          </Button>
          <Typography variant="caption" color="text.secondary">
            {total} cliente{total === 1 ? "" : "s"} activo{total === 1 ? "" : "s"}
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

        <Paper variant="outlined" sx={{ borderRadius: 1.25, overflow: "hidden" }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <StyledHeaderCell>ID</StyledHeaderCell>
                  <StyledHeaderCell>Nombre</StyledHeaderCell>
                  <StyledHeaderCell>Usuario</StyledHeaderCell>
                  <StyledHeaderCell>Contraseña</StyledHeaderCell>
                  <StyledHeaderCell>Email</StyledHeaderCell>
                  <StyledHeaderCell>Servicio Sort</StyledHeaderCell>
                  <StyledHeaderCell>Roles</StyledHeaderCell>
                  <StyledHeaderCell align="center">Vehiculos</StyledHeaderCell>
                  <StyledHeaderCell align="center">Cuadrillas</StyledHeaderCell>
                  <StyledHeaderCell align="center">Estado</StyledHeaderCell>
                  <StyledHeaderCell align="center">Acciones</StyledHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={26} />
                    </TableCell>
                  </TableRow>
                ) : clientes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">No hay clientes para mostrar.</Typography>
                    </TableCell>
                  </TableRow>
                ) : clientes.map((c) => (
                  <React.Fragment key={c.cod_servicio_cliente}>
                    <TableRow>
                      <TableCell
                        colSpan={11}
                        sx={{
                          py: 0.75,
                          px: 1.25,
                          fontSize: "0.8rem",
                          fontWeight: 800,
                          bgcolor: (theme) => theme.palette.mode === "dark" ? "primary.900" : "primary.50",
                          color: "primary.dark",
                          borderTop: "2px solid",
                          borderColor: "primary.main",
                          borderLeft: "4px solid",
                          borderLeftColor: c.usuarios.length ? "primary.main" : "grey.400",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 0.75 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                            <BusinessIcon fontSize="small" sx={{ color: c.usuarios.length ? "primary.main" : "grey.500" }} />
                            <span>
                              {c.razon_social} {c.cod_cliente ? `(${c.cod_cliente})` : ""}
                            </span>
                            <Typography variant="caption" color="text.secondary">
                              Servicio {c.cod_servicio_cliente}{c.servicio_descripcion ? ` — ${c.servicio_descripcion}` : ""}
                            </Typography>
                            <Chip
                              label={c.usuarios.length ? `${c.usuarios.length} usuario${c.usuarios.length === 1 ? "" : "s"}` : "Sin usuario TRS Rastreo"}
                              size="small"
                              sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700, bgcolor: "background.paper" }}
                            />
                          </Box>
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title="Adicionar usuario TRS Rastreo">
                              <IconButton size="small" onClick={() => abrirAdicionarUsuario(c)} sx={{ bgcolor: "background.paper" }}>
                                <PersonAddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Historial">
                              <IconButton size="small" onClick={() => abrirHistorial(c)} sx={{ bgcolor: "background.paper" }}>
                                <HistoryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Box>
                      </TableCell>
                    </TableRow>
                    {c.usuarios.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} sx={{ ...cellSx, py: 0.75 }}>
                          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: "text.disabled" }}>
                            <PersonOffIcon sx={{ fontSize: 16 }} />
                            <Typography variant="caption">Este cliente todavia no tiene usuario creado en TRS Rastreo.</Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ) : c.usuarios.map((u) => (
                      <TableRow key={u.id} hover sx={u.usu_activo ? {} : { opacity: 0.6 }}>
                        <TableCell sx={cellSx}>{u.id}</TableCell>
                        <TableCell sx={cellSx}>{u.usu_nombre || "-"}</TableCell>
                        <TableCell sx={cellSx}>{u.usu_username || "-"}</TableCell>
                        <TableCell sx={cellSx}>
                          <PasswordCell
                            password={u.usu_password_descr}
                            visible={!!passwordsVisibles[u.id]}
                            onToggleVisible={() => togglePassword(u.id)}
                            onCopy={() => copiarPassword(u.usu_password_descr)}
                          />
                        </TableCell>
                        <TableCell sx={cellSx}>{u.usu_email || "-"}</TableCell>
                        <TableCell sx={cellSx}>
                          <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>{u.usu_cliente ?? "-"}</Typography>
                        </TableCell>
                        <TableCell sx={cellSx}>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {(u.roles || []).map((r) => (
                              <Chip key={r.id} label={r.nombre} size="small" sx={{ height: 20, fontSize: "0.68rem" }} />
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell sx={cellSx} align="center">
                          <CountBadge value={(u.vehiculos_relacionados || []).length} icon={<DirectionsCarIcon />} color="info" />
                        </TableCell>
                        <TableCell sx={cellSx} align="center">
                          <CountBadge
                            value={(u.relacion_usuarios || []).filter((r) => r.table_rel === "cuadrillas").length}
                            icon={<GroupsIcon />}
                            color="secondary"
                          />
                        </TableCell>
                        <TableCell sx={cellSx} align="center">
                          <Chip
                            label={u.usu_activo ? "Activo" : "Inactivo"}
                            size="small"
                            color={u.usu_activo ? "success" : "default"}
                            sx={{ height: 20, fontSize: "0.68rem" }}
                          />
                        </TableCell>
                        <TableCell sx={cellSx} align="center">
                          <Tooltip title="Ver detalle">
                            <IconButton size="small" onClick={() => setDetalle(u)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar usuario">
                            <IconButton size="small" onClick={() => abrirEditarUsuario(u, c)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[15, 30, 50, 100]}
            labelRowsPerPage="Limite"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
          />
        </Paper>
      </Box>

      <Dialog open={Boolean(detalle)} onClose={() => setDetalle(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "1rem", fontWeight: 800 }}>
          {detalle?.usu_nombre || detalle?.usu_username}
          <IconButton size="small" onClick={() => setDetalle(null)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="caption" color="text.secondary">Usuario / Email</Typography>
              <Typography variant="body2">
                {detalle?.usu_username} · {detalle?.usu_email || "sin email"}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">Contraseña</Typography>
              <Box sx={{ mt: 0.25 }}>
                <PasswordCell
                  password={detalle?.usu_password_descr}
                  visible={!!passwordsVisibles[detalle?.id]}
                  onToggleVisible={() => togglePassword(detalle?.id)}
                  onCopy={() => copiarPassword(detalle?.usu_password_descr)}
                />
              </Box>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">Permisos</Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                {(detalle?.permissions || []).length
                  ? detalle.permissions.map((p) => (
                    <Chip key={p} label={p} size="small" variant="outlined" sx={{ height: 20, fontSize: "0.65rem" }} />
                  ))
                  : <Typography variant="caption" color="text.secondary">Sin permisos</Typography>}
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={800} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                <DirectionsCarIcon fontSize="small" /> Vehiculos relacionados ({(detalle?.vehiculos_relacionados || []).length})
              </Typography>
              {(detalle?.vehiculos_relacionados || []).length === 0 ? (
                <Typography variant="caption" color="text.secondary">Sin vehiculos relacionados</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={cellSx}>Placa / Alias</TableCell>
                      <TableCell sx={cellSx}>Cuadrilla</TableCell>
                      <TableCell sx={cellSx} align="center">Relacion</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detalle.vehiculos_relacionados.map((v) => (
                      <TableRow key={v.usuariovehiculo_id}>
                        <TableCell sx={cellSx}>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.78rem" }}>{v.vehiculo?.veh_placa}</Typography>
                          <Typography variant="caption" color="text.secondary">{v.vehiculo?.veh_alias}</Typography>
                        </TableCell>
                        <TableCell sx={cellSx}>{v.cuadrilla_info?.cuadrilla_nombre || "-"}</TableCell>
                        <TableCell sx={cellSx} align="center">
                          <Chip
                            label={v.estado_relacion ? "Activa" : "Inactiva"}
                            size="small"
                            color={v.estado_relacion ? "success" : "default"}
                            sx={{ height: 18, fontSize: "0.62rem" }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={800} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                <GroupsIcon fontSize="small" /> Cuadrillas ({cuadrillasRelacionadas.length})
              </Typography>
              {cuadrillasRelacionadas.length === 0 ? (
                <Typography variant="caption" color="text.secondary">Sin cuadrillas relacionadas</Typography>
              ) : (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {cuadrillasRelacionadas.map((r) => (
                    <Chip
                      key={r.id}
                      label={r.cuadrilla_info?.cua_descripcion || `#${r.for_id}`}
                      size="small"
                      color={r.usurel_activo ? "primary" : "default"}
                      variant="outlined"
                    />
                  ))}
                </Stack>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={800} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                <PinDropIcon fontSize="small" /> Geocercas ({geocercasRelacionadas.length})
              </Typography>
              {geocercasRelacionadas.length === 0 ? (
                <Typography variant="caption" color="text.secondary">Sin geocercas relacionadas</Typography>
              ) : (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {geocercasRelacionadas.map((r) => (
                    <Chip
                      key={r.id}
                      label={r.geocerca_info?.geo_nombre || `#${r.for_id}`}
                      size="small"
                      color={r.usurel_activo ? "primary" : "default"}
                      variant="outlined"
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(nuevoUsuarioCliente)}
        onClose={() => { setNuevoUsuarioCliente(null); setEditUsuarioId(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "1rem", fontWeight: 800 }}>
          {editUsuarioId ? "Editar usuario" : "Adicionar usuario"}
          <IconButton size="small" onClick={() => { setNuevoUsuarioCliente(null); setEditUsuarioId(null); }}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25} sx={{ pt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Cliente: {nuevoUsuarioCliente?.razon_social} — Servicio {nuevoUsuarioCliente?.cod_servicio_cliente}
            </Typography>
            <TextField
              size="small" label="Nombre" value={nuevoUsuarioForm.usu_nombre}
              onChange={(e) => setNuevoUsuarioForm((p) => ({ ...p, usu_nombre: e.target.value }))}
            />
            <TextField
              size="small" label="Usuario" value={nuevoUsuarioForm.usu_username}
              onChange={(e) => setNuevoUsuarioForm((p) => ({ ...p, usu_username: e.target.value }))}
            />
            <TextField
              size="small"
              label={editUsuarioId ? "Nueva contraseña" : "Contraseña"}
              helperText={editUsuarioId ? "Dejar en blanco para no cambiarla" : ""}
              value={nuevoUsuarioForm.usu_password}
              onChange={(e) => setNuevoUsuarioForm((p) => ({ ...p, usu_password: e.target.value }))}
            />
            <TextField
              size="small" label="Email" value={nuevoUsuarioForm.usu_email}
              onChange={(e) => setNuevoUsuarioForm((p) => ({ ...p, usu_email: e.target.value }))}
            />
            <TextField
              select
              size="small"
              label="Tipo de usuario"
              value={nuevoUsuarioForm.usu_tipo}
              onChange={(e) => setNuevoUsuarioForm((p) => ({ ...p, usu_tipo: Number(e.target.value) }))}
            >
              {USU_TIPOS.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </TextField>
            {editUsuarioId && (
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={nuevoUsuarioForm.usu_activo}
                    onChange={(e) => setNuevoUsuarioForm((p) => ({ ...p, usu_activo: e.target.checked }))}
                  />
                )}
                label="Activo"
              />
            )}
            {errorUsuario && <Alert severity="error" sx={{ fontSize: "0.75rem" }}>{errorUsuario}</Alert>}
          </Stack>
        </DialogContent>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 1.5 }}>
          <Button size="small" onClick={() => { setNuevoUsuarioCliente(null); setEditUsuarioId(null); }} disabled={guardandoUsuario}>Cancelar</Button>
          <Button
            size="small"
            variant="contained"
            onClick={guardarNuevoUsuario}
            disabled={guardandoUsuario}
            startIcon={guardandoUsuario ? <CircularProgress size={14} color="inherit" /> : null}
          >
            Guardar
          </Button>
        </Box>
      </Dialog>

      <Dialog
        open={Boolean(historialCliente)}
        onClose={() => setHistorialCliente(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: "90vh" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "1rem", fontWeight: 800 }}>
          Historial — {historialCliente?.razon_social} ({historialCliente?.cod_servicio_cliente})
          <IconButton size="small" onClick={() => setHistorialCliente(null)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {historialCliente && (
            <HistorialCliente
              codServicioClienteProp={historialCliente.cod_servicio_cliente}
              embedded
              onBack={() => setHistorialCliente(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={copiado}
        autoHideDuration={2000}
        onClose={() => setCopiado(false)}
        message="Contraseña copiada al portapapeles"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </DashboardLayout>
  );
}
